import "./ManageUser.scss";
import { faChevronDown, faChevronRight } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { ColumnDef, createColumnHelper } from "@tanstack/react-table";
import AbunTable from "components/AbunTable/AbunTable";
import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { adminGetAIShareWidgets, adminGetAIShareWidgetDetail } from "../../utils/api"; 
import "./integration.scss";

export interface ShareWidgetData {
    id: number
    widget_id: string
    name: string
    prompt_template: string
    text_before_button: string
    selected_llms: string[]
    style: string
    html_code: string
    html_code_dark: string
    is_active: boolean
    is_processing: boolean
    total_clicks: number
    created_at: string
    updated_at: string
    total_clicks_by_llm: Record<string, number>
    destination_urls_stats: Record<string, any>
    // Admin-specific fields
    user_id?: number
    user_email?: string
    website_domain?: string
    website_name?: string
    user_plan?: string
}

interface AdminShareWidgetResponse {
    status: string
    data: {
        widgets: ShareWidgetData[]
        total_count: number
        returned_count: number
        debug_info?: any
    }
}

export default function AIShareWidget(props: { 
    shareWidgets?: Array<ShareWidgetData>, 
    showDateColumn?: boolean, 
    isAdmin?: boolean,
    userId?: string,  // For filtering by specific user in admin
    searchTerm?: string  // For searching
}) {
    // ---------------------- STATE ----------------------
    const [showShareWidgets, setShowShareWidgets] = useState(false);
    const [adminShareWidgets, setAdminShareWidgets] = useState<ShareWidgetData[]>([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [loadingRowId, setLoadingRowId] = useState<string | null>(null);

    // ---------------------- HOOKS ----------------------
    const navigate = useNavigate();

    // ---------------------- NON STATE CONSTANTS ----------------------
    const pageSizes = [10, 50, 150, 250, 500];
    

    // ---------------------- ADMIN API CALLS ----------------------
    const fetchAdminShareWidgets = async (userId?: string, search?: string) => {
        setLoading(true);
        setError(null);
        
        try {
            // Use the API function instead of fetch
            const response = await adminGetAIShareWidgets(userId, search);
            
            if (response.status === 200 && response.data?.status === 'success') {
                setAdminShareWidgets(response.data.data.widgets || []);
            } else {
                throw new Error(response.data?.message || 'Failed to fetch share widgets');
            }
        } catch (err: any) {
            // Handle different error types
            let errorMessage = 'Failed to fetch share widgets';
            if (err.response?.data?.message) {
                errorMessage = err.response.data.message;
            } else if (err.message) {
                errorMessage = err.message;
            }
            
            setError(errorMessage);
        } finally {
            setLoading(false);
        }
    };

    // ---------------------- ADMIN SHARE WIDGET DATA FETCH ----------------------
    const fetchAdminShareWidgetDetail = async (widgetId: string) => {
        setLoadingRowId(widgetId);
        
        try {
            // Use the API function instead of fetch
            const response = await adminGetAIShareWidgetDetail(widgetId);
            
            if (response.status === 200 && response.data?.status === 'success' && response.data?.widget_data) {
                const widgetData = response.data.widget_data;
                
                // Navigate to the share widget view - adjust this route as needed
                navigate(`/admin/view-share-widget/${widgetData.widget_id}`, {
                    state: {
                        widgetId: widgetData.widget_id,
                        widgetName: widgetData.name,
                        widgetData: widgetData,
                        fromAdmin: true,
                        navigationTimestamp: Date.now()
                    },
                    replace: false
                });
            } else {
                throw new Error(response.data?.message || 'Invalid response format');
            }
        } catch (err: any) {
            console.error('Error fetching admin share widget detail:', err);
            
            // Handle different error types
            let errorMessage = 'Failed to fetch share widget detail';
            if (err.response?.data?.message) {
                errorMessage = err.response.data.message;
            } else if (err.message) {
                errorMessage = err.message;
            }
            
            setError(errorMessage);
        } finally {
            setLoadingRowId(null);
        }
    };

    // ---------------------- EFFECTS ----------------------
    useEffect(() => {
        if (props.isAdmin && showShareWidgets) {
            fetchAdminShareWidgets(props.userId, props.searchTerm);
        }
    }, [props.isAdmin, showShareWidgets, props.userId, props.searchTerm]);

    useEffect(() => {
        if (props.isAdmin && showShareWidgets && props.userId) {
            fetchAdminShareWidgets(props.userId, props.searchTerm);
        }
    }, [props.userId]);


    // ---------------------- HELPER FUNCTIONS ----------------------
    const truncateWidgetId = (widgetId: string, maxLength: number = 20) => {
        return widgetId.length > maxLength ? `${widgetId.substring(0, maxLength)}...` : widgetId;
    };

    const formatDate = (dateString: string): string => {
        try {
            const date = new Date(dateString);
            
            // Get day with ordinal suffix
            const day = date.getDate();
            const getOrdinalSuffix = (day: number): string => {
                if (day > 3 && day < 21) return 'th';
                switch (day % 10) {
                    case 1: return 'st';
                    case 2: return 'nd';
                    case 3: return 'rd';
                    default: return 'th';
                }
            };
            
            // Get month abbreviation
            const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
            const month = months[date.getMonth()];
            
            // Get year (last 2 digits)
            const year = date.getFullYear().toString().slice(-2);
            
            // Get time in 12-hour format
            const hours = date.getHours();
            const minutes = date.getMinutes().toString().padStart(2, '0');
            const ampm = hours >= 12 ? 'PM' : 'AM';
            const displayHours = hours % 12 || 12;
            
            return `${day}${getOrdinalSuffix(day)} ${month}, ${year}, ${displayHours}:${minutes} ${ampm}`;
        } catch (error) {
            console.error('Error formatting date:', error);
            return dateString; // Return original string if formatting fails
        }
    };

    // ---------------------- HANDLERS ----------------------
    const handleViewShareWidget = (shareWidget: ShareWidgetData) => {
        if (!shareWidget.widget_id) {
            console.error("Invalid share widget data");
            return;
        }
        fetchAdminShareWidgetDetail(shareWidget.widget_id.trim());
    };

    // ---------------------- TABLE COLUMN DEFS ----------------------
    const columnHelper = createColumnHelper<ShareWidgetData>();
    const columnDefs: ColumnDef<ShareWidgetData, any>[] = [
        columnHelper.accessor((row: ShareWidgetData) => row.widget_id, {
            id: 'widget_id',
            header: "Widget ID",
            cell: props => {
                const fullId = props.row.original.widget_id;
                const truncatedId = truncateWidgetId(fullId);
                const isRowLoading = loadingRowId === fullId;
                
                return (
                    <span 
                        title={fullId} 
                        style={{ 
                            cursor: fullId.length > 20 ? 'help' : 'default',
                            opacity: isRowLoading ? 0.6 : 1
                        }}
                    >
                        {truncatedId}
                    </span>
                );
            },
            enableGlobalFilter: true,
            enableSorting: true
        }),

        columnHelper.accessor((row: ShareWidgetData) => row.name, {
            id: 'name',
            header: "Widget Name",
            cell: info => {
                const value = info.getValue();
                const isRowLoading = loadingRowId === info.row.original.widget_id;
                return (
                    <span style={{ opacity: isRowLoading ? 0.6 : 1 }}>
                        {value || "---"}
                    </span>
                );
            },
            enableGlobalFilter: true,
            enableSorting: true
        }),

        columnHelper.accessor((row: ShareWidgetData) => row.created_at, {
            id: 'created_at',
            header: "Created On",
            cell: info => {
                const isRowLoading = loadingRowId === info.row.original.widget_id;
                return (
                    <span style={{ opacity: isRowLoading ? 0.6 : 1 }}>
                        {formatDate(info.getValue())}
                    </span>
                );
            },
            enableGlobalFilter: false,
        }),
    ];

    columnDefs.push(
        columnHelper.display({
            id: 'view_widget',
            header: "View Widget",
            cell: props => {
                const original = props.row.original;
                const isRowLoading = loadingRowId === original.widget_id;
                
                // For admin, use button that calls our handler
                return (
                    <button 
                        className="button is-primary"
                        onClick={(e) => {
                            e.stopPropagation(); 
                            handleViewShareWidget(original);
                        }}
                        disabled={isRowLoading}
                        style={{ 
                            backgroundColor: isRowLoading ? "#86a5ec" : "",
                            cursor: isRowLoading ? "not-allowed" : "pointer"
                        }}
                    >
                        {isRowLoading ? "Loading..." : "View"}
                    </button>
                );
            },
            enableGlobalFilter: false,
        })
    );

    // ---------------------- DATA SOURCE ----------------------
    const dataToUse = props.isAdmin ? adminShareWidgets : (props.shareWidgets || []);

    // ---------------------- MAIN RENDER CODE ----------------------
    return (
        <div className={"card w-100 mt-4"}>
            <header
                className="card-header is-clickable"
                onClick={() => setShowShareWidgets(!showShareWidgets)}
                >
                <p className="card-header-title">
                    AI Share Widgets 
                </p>
                <span className="card-header-icon">
                  <FontAwesomeIcon icon={showShareWidgets ? faChevronDown : faChevronRight} />
                </span>
            </header>
            {showShareWidgets && (
                <div className={"card-content"}>
                    <div className={"content"}>
                        {error && (
                            <div className="notification is-danger">
                                <button className="delete" onClick={() => setError(null)}></button>
                                {error}
                            </div>
                        )}
                        
                        <div className={"all-users-container"}>
                            <AbunTable 
                                tableContentName={"AI Share Widgets"}
                                tableData={dataToUse}
                                columnDefs={columnDefs}
                                pageSizes={pageSizes}
                                initialPageSize={pageSizes[0]}
                                noDataText={props.isAdmin && loading ? "Loading..." : "No Share Widgets Found"}
                                enableSorting={true}
                                searchboxPlaceholderText={"Search Share Widgets..."} 
                            />
                        </div>
                    </div>
                </div>
            )}
        </div>
    )
}
